import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Header } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { getWorkerTypeLabel } from '../../lib/utils';
import { toast } from 'sonner';
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  UserCheck,
  UserX,
  Phone,
  CreditCard,
  DollarSign,
  Shield,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Updated worker types with new grabman and driver
const workerTypes = [
  { value: 'cane_cutter', label: 'Cane Cutter', requiresRate: false },
  { value: 'field_personnel', label: 'Field Personnel', requiresRate: false },
  { value: 'boss', label: 'Group Leader', requiresRate: false },
  { value: 'grabman', label: 'Grabman', requiresRate: true },
  { value: 'driver', label: 'Driver', requiresRate: true },
];

export function WorkersPage() {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    worker_type: 'cane_cutter',
    rate: '',
    ssb_number: '',
    phone: '',
    has_ssb: true,
    is_active: true,
  });

  useEffect(() => {
    fetchWorkers();
  }, []);

  const fetchWorkers = async () => {
    try {
      const response = await axios.get(`${API_URL}/workers`);
      setWorkers(response.data);
    } catch (error) {
      toast.error('Failed to fetch workers');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (worker = null) => {
    if (worker) {
      setSelectedWorker(worker);
      setFormData({
        name: worker.name,
        worker_type: worker.worker_type,
        rate: worker.rate || '',
        ssb_number: worker.ssb_number || '',
        phone: worker.phone || '',
        has_ssb: worker.has_ssb !== undefined ? worker.has_ssb : true,
        is_active: worker.is_active,
      });
    } else {
      setSelectedWorker(null);
      setFormData({
        name: '',
        worker_type: 'cane_cutter',
        rate: '',
        ssb_number: '',
        phone: '',
        has_ssb: true,
        is_active: true,
      });
    }
    setDialogOpen(true);
  };

  const requiresRate = (workerType) => {
    const type = workerTypes.find(t => t.value === workerType);
    return type?.requiresRate || false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    // Validate rate for grabman/driver
    if (requiresRate(formData.worker_type) && (!formData.rate || parseFloat(formData.rate) <= 0)) {
      toast.error('Rate is required for Grabman and Driver types');
      setSaving(false);
      return;
    }

    const submitData = {
      ...formData,
      rate: requiresRate(formData.worker_type) ? parseFloat(formData.rate) : null,
    };

    try {
      if (selectedWorker) {
        await axios.put(`${API_URL}/workers/${selectedWorker.id}`, submitData);
        toast.success('Worker updated successfully');
      } else {
        await axios.post(`${API_URL}/workers`, submitData);
        toast.success('Worker created successfully');
      }
      setDialogOpen(false);
      fetchWorkers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save worker');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/workers/${selectedWorker.id}`);
      toast.success('Worker deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedWorker(null);
      fetchWorkers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete worker');
    }
  };

  const filteredWorkers = workers.filter((worker) => {
    const matchesSearch = worker.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (worker.ssb_number && worker.ssb_number.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesType = filterType === 'all' || worker.worker_type === filterType;
    const matchesActive = filterActive === 'all' || 
      (filterActive === 'active' && worker.is_active) ||
      (filterActive === 'inactive' && !worker.is_active);
    return matchesSearch && matchesType && matchesActive;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header
        title="Workers"
        subtitle={`Manage your ${workers.length} workers`}
        actions={
          <Button onClick={() => handleOpenDialog()} className="gap-2" data-testid="add-worker-btn">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Worker</span>
          </Button>
        }
      />

      <div className="flex-1 p-4 lg:p-8 space-y-6">
        {/* Filters */}
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search workers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-workers"
                />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-type">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {workerTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterActive} onValueChange={setFilterActive}>
                <SelectTrigger className="w-full sm:w-[150px]" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Workers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredWorkers.map((worker, index) => (
            <Card 
              key={worker.id} 
              className="card-base animate-slide-up hover:shadow-md transition-shadow"
              style={{ animationDelay: `${index * 50}ms` }}
              data-testid={`worker-card-${worker.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      worker.is_active ? 'bg-primary/10' : 'bg-muted'
                    }`}>
                      <Users className={`w-6 h-6 ${
                        worker.is_active ? 'text-primary' : 'text-muted-foreground'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-semibold">{worker.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {getWorkerTypeLabel(worker.worker_type)}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    <Badge variant={worker.is_active ? 'default' : 'secondary'}>
                      {worker.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    {!worker.has_ssb && (
                      <Badge variant="outline" className="text-xs">
                        No SSB
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm">
                  {/* Show rate for grabman/driver */}
                  {(worker.worker_type === 'grabman' || worker.worker_type === 'driver') && worker.rate && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <DollarSign className="w-4 h-4" />
                      <span>Rate: ${worker.rate.toFixed(2)}</span>
                    </div>
                  )}
                  {worker.ssb_number && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <CreditCard className="w-4 h-4" />
                      <span>SSB: {worker.ssb_number}</span>
                    </div>
                  )}
                  {worker.phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="w-4 h-4" />
                      <span>{worker.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Shield className="w-4 h-4" />
                    <span>SSB Deduction: {worker.has_ssb !== false ? 'Yes' : 'No'}</span>
                  </div>
                </div>

                <div className="mt-4 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleOpenDialog(worker)}
                    data-testid={`edit-worker-${worker.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    onClick={() => {
                      setSelectedWorker(worker);
                      setDeleteDialogOpen(true);
                    }}
                    data-testid={`delete-worker-${worker.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredWorkers.length === 0 && (
          <Card className="card-base">
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">
                {searchQuery || filterType !== 'all' || filterActive !== 'all'
                  ? 'No workers match your filters.'
                  : 'No workers yet. Add your first worker to get started.'}
              </p>
              {!searchQuery && filterType === 'all' && filterActive === 'all' && (
                <Button onClick={() => handleOpenDialog()} className="mt-4" data-testid="add-first-worker">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Worker
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedWorker ? 'Edit Worker' : 'Add New Worker'}
            </DialogTitle>
            <DialogDescription>
              {selectedWorker ? 'Update worker information.' : 'Add a new worker to your farm group.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter worker name"
                  required
                  data-testid="worker-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="worker_type">Worker Type *</Label>
                <Select
                  value={formData.worker_type}
                  onValueChange={(value) => setFormData({ ...formData, worker_type: value, rate: requiresRate(value) ? formData.rate : '' })}
                >
                  <SelectTrigger data-testid="worker-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {workerTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label} {type.requiresRate && '(Rate Required)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Rate field - only shown for grabman and driver */}
              {requiresRate(formData.worker_type) && (
                <div className="space-y-2">
                  <Label htmlFor="rate">Rate * (multiplier for tons)</Label>
                  <Input
                    id="rate"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.rate}
                    onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                    placeholder="Enter rate (e.g., 0.50)"
                    required
                    data-testid="worker-rate-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Pay calculation: Rate × Tons(Factory Price)
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="ssb_number">SSB Number</Label>
                <Input
                  id="ssb_number"
                  value={formData.ssb_number}
                  onChange={(e) => setFormData({ ...formData, ssb_number: e.target.value })}
                  placeholder="SSB-001"
                  data-testid="worker-ssb-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+501 123-4567"
                  data-testid="worker-phone-input"
                />
              </div>

              {/* Has SSB Checkbox */}
              <div className="flex items-center space-x-2 p-3 border rounded-lg bg-muted/50">
                <Checkbox
                  id="has_ssb"
                  checked={formData.has_ssb}
                  onCheckedChange={(checked) => setFormData({ ...formData, has_ssb: checked })}
                  data-testid="worker-has-ssb-checkbox"
                />
                <div className="grid gap-1.5 leading-none">
                  <Label htmlFor="has_ssb" className="cursor-pointer font-medium">
                    SSB Deduction
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {formData.has_ssb 
                      ? 'SSB will be deducted from gross pay' 
                      : 'No SSB will be deducted from gross pay'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded border-input"
                  data-testid="worker-active-checkbox"
                />
                <Label htmlFor="is_active" className="cursor-pointer">
                  Active Worker
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-worker-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Worker'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Worker</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedWorker?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-worker"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
